import { z } from "zod";

/**
 * Free trial form validation schema with step-by-step validation
 */
export const freeTrialFormSchema = z.object({
  // Step 1: Contact Details
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "First name can only contain letters, spaces, hyphens, and apostrophes"),
  
  lastName: z
    .string()
    .trim()
    .min(1, "Last name is required")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Last name can only contain letters, spaces, hyphens, and apostrophes"),
  
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters")
    .toLowerCase()
    .refine(
      (email) => {
        const personalEmailDomains = [
          'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
          'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
          'yandex.com', 'zoho.com', 'gmx.com', 'live.com', 'msn.com'
        ];
        const domain = email.split('@')[1];
        return !personalEmailDomains.includes(domain);
      },
      { message: "Please use a work email address" }
    ),
  
  businessName: z
    .string()
    .trim()
    .min(1, "Business name is required")
    .max(100, "Business name must be less than 100 characters"),
  
  country: z
    .string()
    .min(1, "Please select a country"),
  
  // Step 2: Industry
  industry: z
    .string()
    .min(1, "Please select an industry"),
  
  // Step 3: Platform & Tech Stack
  currentPlatform: z
    .string()
    .min(1, "Please select your current platform"),
  
  otherTech: z
    .array(z.string())
    .default([]),
  
  // Step 4: Team Members (optional)
  teamMembers: z
    .array(z.string().email("Invalid email address").or(z.literal("")))
    .default([]),
  
  // Honeypot field - should remain empty for legitimate users
  website: z
    .string()
    .max(0, "Spam detected")
    .optional()
    .or(z.literal("")),
});

export type FreeTrialFormData = z.infer<typeof freeTrialFormSchema>;

/**
 * Step-specific validation schemas for progressive validation
 */
export const freeTrialStepSchemas = {
  step1: freeTrialFormSchema.pick({
    firstName: true,
    lastName: true,
    email: true,
    businessName: true,
    country: true,
  }),
  
  step2: freeTrialFormSchema.pick({
    industry: true,
  }),
  
  step3: freeTrialFormSchema.pick({
    currentPlatform: true,
    otherTech: true,
  }),
  
  step4: freeTrialFormSchema.pick({
    teamMembers: true,
  }),
};
